"use client";

import { EXTERNAL_LINKS } from "@/lib/constants";
import { useEffect, useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import api from "@/lib/api";
import { User } from "lucide-react";
import { useToast } from "@/hooks/useToast";

interface Teacher {
  id: string;
  name: string;
  bio: string;
  specialties: string[];
  imageUrl?: string;
}

interface Review {
  id: string;
  authorName: string;
  rating: number;
  content: string;
}

function HomeContent() {
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);

  useEffect(() => {
    // Check if user was redirected due to auth requirement
    if (searchParams.get("auth") === "required") {
      toast.info("로그인이 필요한 페이지입니다. 먼저 로그인해주세요.");
      // Clean up URL
      window.history.replaceState({}, "", "/");
    }

    const fetchTeachers = async () => {
      try {
        const response = await api.get("/api/teachers");
        setTeachers(response.data.teachers);
      } catch (error) {
        console.error("Error fetching teachers:", error);
      }
    };

    const fetchReviews = async () => {
      try {
        const response = await api.get("/api/reviews");
        setReviews(response.data.reviews);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };

    fetchTeachers();
    fetchReviews();
    // toast is a stable reference from useToastStore but not memoized in useToast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div className="bg-surface-muted">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="relative bg-gradient-to-r from-blue-100 to-purple-100 rounded-3xl overflow-hidden h-96">
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <h2 className="text-4xl font-bold text-blue-600 mb-4">
              온라인 요가의 정석
              <br />
              루트인 요가
            </h2>
            <p className="text-text-muted mb-8">
              우리집 거실이 요가원으로,
              <br />
              전문 강사와 함께하는 온라인 요가 클래스
            </p>
            <a
              href={EXTERNAL_LINKS.SMART_STORE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-full shadow-md hover:bg-primary-hover transition"
            >
              수업권 구매
            </a>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h3 className="text-3xl font-bold text-center text-blue-500 mb-12">
          리얼 후기
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.length > 0 ? (
            reviews.map((review) => (
              <div
                key={review.id}
                className="bg-blue-50 rounded-2xl p-6 text-center"
              >
                <h4 className="font-bold text-lg text-text mb-2">
                  {review.authorName}
                </h4>
                <div className="flex justify-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={
                        star <= review.rating
                          ? "text-yellow-400"
                          : "text-text-subtle"
                      }
                    >
                      ★
                    </span>
                  ))}
                </div>
                <p className="text-text">{review.content}</p>
              </div>
            ))
          ) : (
            <div className="col-span-3 text-center text-text-muted py-8">
              아직 등록된 리뷰가 없습니다
            </div>
          )}
        </div>
      </section>

      {/* Instructors Section */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h3 className="text-3xl font-bold text-center text-blue-500 mb-12">
          전문 강사진
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {teachers.map((teacher) => (
            <div
              key={teacher.id}
              className="bg-surface-subtle rounded-2xl overflow-hidden"
            >
              {teacher.imageUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={teacher.imageUrl}
                  alt={teacher.name}
                  className="w-full aspect-square object-cover"
                />
              ) : (
                <div className="w-full aspect-square bg-surface-subtle flex items-center justify-center">
                  <User className="w-20 h-20 text-text-subtle" />
                </div>
              )}
              <div className="p-4 text-center">
                <h4 className="font-bold text-lg text-text">{teacher.name}</h4>
                <p className="text-text">{teacher.specialties.join(", ")}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 루트인 요가만의 특별함 Section */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="bg-surface rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-center text-blue-500 mb-8">
            루트인 요가만의 특별함
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 border border-border rounded-xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <h4 className="font-bold text-text mb-2">전문 강사진</h4>
              <p className="text-sm text-text-muted">
                자격증을 보유한 전문 요가 강사들이 직접 지도
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h4 className="font-bold text-text mb-2">풍부한 시간표</h4>
              <p className="text-sm text-text-muted">
                언제든지 원하는 시간에 수업 참여 가능
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-xl">
              <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  />
                </svg>
              </div>
              <h4 className="font-bold text-text mb-2">맞춤형 프로그램</h4>
              <p className="text-sm text-text-muted">
                개인 수준에 맞는 프로그램
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function Home() {
  return (
    <Suspense fallback={<div className="p-8 text-center">로딩 중...</div>}>
      <HomeContent />
    </Suspense>
  );
}
